import React from "react";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SettingsForm from "./SettingsForm";
import NurseryStep from "./NurseryStep";
import { SETTINGS_KEYS } from "./HierarchyManagementConstants";
import type { WizardData } from "./HierarchyManagement";
import type { Nursery } from "../types/auth";

interface WizardModalProps {
  show: boolean;
  onHide: () => void;
  modalType: "group" | "location" | "nursery" | null;
  modalAction: "create" | "edit" | "assign-nursery";
  wizardStep: number;
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  nurseryMode: "attach" | "create";
  setNurseryMode: React.Dispatch<React.SetStateAction<"attach" | "create">>;
  override: Record<string, boolean>;
  setOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  nurseryOverride: Record<string, boolean>;
  setNurseryOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  modalError: string | null;
  modalSuccess: string | null;
  actionLoading: boolean;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  availableNurseries: Nursery[];
}

const WizardModal: React.FC<WizardModalProps> = ({
  show,
  onHide,
  modalType,
  modalAction,
  wizardStep,
  wizardData,
  setWizardData,
  nurseryMode,
  setNurseryMode,
  override,
  setOverride,
  nurseryOverride,
  setNurseryOverride,
  modalError,
  modalSuccess,
  actionLoading,
  onNext,
  onBack,
  onSubmit,
  availableNurseries,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'wizardModal' });
  const wizardTotalSteps =
    modalAction === "assign-nursery" || modalType === "nursery"
      ? 1
      : modalType === "group"
        ? modalAction === "edit"
          ? 1
          : 3
        : modalAction === "edit"
          ? 1
          : 2;

  const renderWizardStep = () => {
    if (modalAction === "assign-nursery" || modalType === "nursery") {
      return (
        <NurseryStep
          modalAction={modalAction}
          wizardData={wizardData}
          setWizardData={setWizardData}
          nurseryMode={nurseryMode}
          setNurseryMode={setNurseryMode}
          nurseryOverride={nurseryOverride}
          setNurseryOverride={setNurseryOverride}
          availableNurseries={availableNurseries}
        />
      );
    }
    if (modalType === "group") {
      switch (wizardStep) {
        case 1:
          return (
            <>
              <h3>{t('groupStep1')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('groupName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.name || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({ ...d, name: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('description')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.description || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({
                      ...d,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={{}}
                setOverride={() => {}}
                showOverride={false}
              />
            </>
          );
        case 2:
          return (
            <>
              <h3>{t('groupStep2')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.location_name || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({
                      ...d,
                      location_name: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationDescription')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.location_description || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({
                      ...d,
                      location_description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={override}
                setOverride={setOverride}
                prefix="location"
                parentData={wizardData}
              />
            </>
          );
        case 3:
          return (
            <NurseryStep
              modalAction={modalAction}
              wizardData={wizardData}
              setWizardData={setWizardData}
              nurseryMode={nurseryMode}
              setNurseryMode={setNurseryMode}
              nurseryOverride={nurseryOverride}
              setNurseryOverride={setNurseryOverride}
              availableNurseries={availableNurseries}
            />
          );
        default:
          return null;
      }
    } else if (modalType === "location") {
      switch (wizardStep) {
        case 1:
          return (
            <>
              <h3>{t('locationStep1')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.location_name || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({
                      ...d,
                      location_name: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationDescription')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.location_description || ""}
                  onChange={(e) =>
                    setWizardData((d: WizardData) => ({
                      ...d,
                      location_description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={override}
                setOverride={setOverride}
                prefix="location"
                parentData={wizardData}
              />
            </>
          );
        case 2:
          return (
            <NurseryStep
              modalAction={modalAction}
              wizardData={wizardData}
              setWizardData={setWizardData}
              nurseryMode={nurseryMode}
              setNurseryMode={setNurseryMode}
              nurseryOverride={nurseryOverride}
              setNurseryOverride={setNurseryOverride}
              availableNurseries={availableNurseries}
            />
          );
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {modalAction === "create"
            ? t('titleCreate')
            : modalAction === "edit"
              ? t('titleEdit')
              : t('titleAssign')}{" "}
          {modalType === 'group' ? t('typeGroup') : modalType === 'location' ? t('typeLocation') : modalType === 'nursery' ? t('typeNursery') : modalType}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalError && <Alert variant="danger">{modalError}</Alert>}
        {modalSuccess && <Alert variant="success">{modalSuccess}</Alert>}
        {renderWizardStep()}
      </Modal.Body>
      <Modal.Footer>
        {wizardStep > 1 && (
          <Button variant="secondary" onClick={onBack}>
            {t('back')}
          </Button>
        )}
        {wizardStep < wizardTotalSteps && (
          <Button variant="primary" onClick={onNext}>
            {t('next')}
          </Button>
        )}
        {wizardStep === wizardTotalSteps && (
          <Button
            variant="success"
            onClick={onSubmit}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <Spinner size="sm" />
            ) : modalAction === "create" ? (
              t('finish')
            ) : modalAction === "assign-nursery" ? (
              t('assign')
            ) : (
              t('saveChanges')
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WizardModal;
